import { useEffect, useLayoutEffect, useRef, useState } from 'react'

interface UseImageDraftEditorOptions {
  /** Current images array — can come from react-hook-form, Redux, local state, anything */
  images: string[]
  onChange: (images: string[]) => void
  uploadFile: (file: File, ext: string) => Promise<string>
  onUploadingChange?: (uploading: boolean) => void
}

interface UseImageDraftEditorReturn {
  isUploading: boolean
  editorOpen: boolean
  editorStartIndex: number
  draftUrls: Set<string>
  uploadingUrls: Set<string>
  failedUrls: Set<string>
  hasPendingImages: boolean
  pendingFilesRef: React.MutableRefObject<Map<string, File>>
  blobUrlsRef: React.MutableRefObject<Set<string>>
  setEditorOpen: (open: boolean) => void
  setEditorStartIndex: (index: number) => void
  createDraftBlobs: (files: File[]) => { blobUrls: string[]; firstNewIndex: number }
  uploadLocalBlobs: (blobUrls: string[]) => Promise<void>
  handleEditorSave: (edits: Record<number, Blob>) => Promise<void>
  handleEditorCancel: () => void
  removeDraftImage: (url: string) => void
}

export function useImageDraftEditor({
  images,
  onChange,
  uploadFile,
  onUploadingChange,
}: UseImageDraftEditorOptions): UseImageDraftEditorReturn {
  const [editorOpen, updateEditorOpen] = useState(false)
  const [editorStartIndex, setEditorStartIndex] = useState(0)
  const [draftUrls, setDraftUrls] = useState<Set<string>>(new Set())
  const [uploadingUrls, setUploadingUrls] = useState<Set<string>>(new Set())
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set())

  const imagesRef = useRef(images)
  const editorImagesRef = useRef<string[] | null>(null)
  const callbacks = useRef({ onChange, uploadFile, onUploadingChange })
  const mounted = useRef(true)
  const pendingFilesRef = useRef<Map<string, File>>(new Map())
  const blobUrlsRef = useRef<Set<string>>(new Set())
  const uploads = useRef<Map<string, Promise<void>>>(new Map())

  const changeImages = (next: string[]) => {
    imagesRef.current = next
    callbacks.current.onChange(next)
  }

  const setEditorOpen = (open: boolean) => {
    if (open) editorImagesRef.current = [...imagesRef.current]
    updateEditorOpen(open)
  }

  const releaseBlob = (url: string) => {
    if (blobUrlsRef.current.delete(url)) URL.revokeObjectURL(url)
    pendingFilesRef.current.delete(url)
  }

  const removeDraftImage = (url: string) => {
    releaseBlob(url)
    setDraftUrls((previous) => new Set(Array.from(previous).filter((item) => item !== url)))
    setFailedUrls((previous) => new Set(Array.from(previous).filter((item) => item !== url)))
  }

  const stageFile = (file: File) => {
    const url = URL.createObjectURL(file)
    blobUrlsRef.current.add(url)
    pendingFilesRef.current.set(url, file)
    return url
  }

  const createDraftBlobs = (files: File[]): { blobUrls: string[]; firstNewIndex: number } => {
    const current = imagesRef.current
    const firstNewIndex = current.length
    const blobUrls = files.map(stageFile)
    changeImages([...current, ...blobUrls])
    setDraftUrls((previous) => new Set([...Array.from(previous), ...blobUrls]))
    return { blobUrls, firstNewIndex }
  }

  const uploadLocalBlobs = async (blobUrls: string[]) => {
    const jobs = blobUrls.map((url) => {
      const existing = uploads.current.get(url)
      if (existing) return existing
      const file = pendingFilesRef.current.get(url)
      if (!file || !imagesRef.current.includes(url) || !mounted.current) return Promise.resolve()

      const upload = callbacks.current.uploadFile
      const wasUploading = uploads.current.size > 0
      const job = Promise.resolve()
        .then(() => upload(file, file.name.split('.').pop() || 'png'))
        .then((uploadedUrl) => {
          if (!uploadedUrl) throw new Error('Image upload returned no URL')
          if (!mounted.current || pendingFilesRef.current.get(url) !== file) return
          const latest = imagesRef.current
          if (latest.includes(url)) changeImages(latest.map((item) => (item === url ? uploadedUrl : item)))
          removeDraftImage(url)
        })
        .catch(() => {
          if (!mounted.current || pendingFilesRef.current.get(url) !== file || !imagesRef.current.includes(url)) return
          setFailedUrls((previous) => new Set([...Array.from(previous), url]))
        })
        .finally(() => {
          uploads.current.delete(url)
          if (!mounted.current) return
          setUploadingUrls(new Set(uploads.current.keys()))
          if (!uploads.current.size) callbacks.current.onUploadingChange?.(false)
        })

      uploads.current.set(url, job)
      setUploadingUrls(new Set(uploads.current.keys()))
      setFailedUrls((previous) => new Set(Array.from(previous).filter((item) => item !== url)))
      if (!wasUploading) callbacks.current.onUploadingChange?.(true)
      return job
    })

    await Promise.all(jobs)
  }

  const handleEditorSave = async (edits: Record<number, Blob>) => {
    updateEditorOpen(false)
    const snapshot = editorImagesRef.current || [...imagesRef.current]
    editorImagesRef.current = null
    const replacements = new Map<string, string>()

    snapshot.forEach((url, index) => {
      if (!edits[index] || !imagesRef.current.includes(url)) return
      const file = new File([edits[index]], 'edited-image.png', { type: 'image/png' })
      replacements.set(url, stageFile(file))
    })

    if (replacements.size) {
      changeImages(imagesRef.current.map((url) => replacements.get(url) || url))
      replacements.forEach((_replacement, url) => removeDraftImage(url))
      setDraftUrls((previous) => new Set([...Array.from(previous), ...Array.from(replacements.values())]))
    }

    await uploadLocalBlobs(snapshot.map((url) => replacements.get(url) || url))
    if (mounted.current && !uploads.current.size) callbacks.current.onUploadingChange?.(false)
  }

  const handleEditorCancel = () => {
    const current = imagesRef.current
    const discarded = current.filter((url) => pendingFilesRef.current.has(url))
    if (discarded.length) {
      changeImages(current.filter((url) => !pendingFilesRef.current.has(url)))
      discarded.forEach(removeDraftImage)
    }
    editorImagesRef.current = null
    updateEditorOpen(false)
    callbacks.current.onUploadingChange?.(uploads.current.size > 0)
  }

  useLayoutEffect(() => {
    imagesRef.current = images
    callbacks.current = { onChange, uploadFile, onUploadingChange }
  }, [images, onChange, uploadFile, onUploadingChange])

  useEffect(() => {
    mounted.current = true
    const blobs = blobUrlsRef.current
    const pending = pendingFilesRef.current
    const jobs = uploads.current

    return () => {
      mounted.current = false
      blobs.forEach((url) => URL.revokeObjectURL(url))
      blobs.clear()
      pending.clear()
      if (jobs.size) callbacks.current.onUploadingChange?.(false)
    }
  }, [])

  return {
    isUploading: uploadingUrls.size > 0,
    editorOpen,
    editorStartIndex,
    draftUrls,
    uploadingUrls,
    failedUrls,
    hasPendingImages: pendingFilesRef.current.size > 0,
    pendingFilesRef,
    blobUrlsRef,
    setEditorOpen,
    setEditorStartIndex,
    createDraftBlobs,
    uploadLocalBlobs,
    handleEditorSave,
    handleEditorCancel,
    removeDraftImage,
  }
}
